export interface HelpStep {
  target: string;
  title: string;
  body: string;
}

export interface HelpGuide {
  title: string;
  summary: string;
  commonTasks: string[];
  steps: HelpStep[];
}

const main = (title: string, body: string): HelpStep => ({
  target: '[data-help="main"]', title, body,
});

const focus = (target: string, title: string, body: string): HelpStep => ({ target, title, body });

const nav = (target: string, title: string, body: string): HelpStep => ({
  target: `[data-help="${target}"]`, title, body,
});

export const HELP_GUIDES: Record<string, HelpGuide> = {
  home: {
    title: 'Home',
    summary: 'Create a task, attach context, or switch to chat from the task composer.',
    commonTasks: ['How do I create a task?', 'How do I use a knowledge base?', 'What is Deep Research?'],
    steps: [
      focus('.praxys-hero', 'Your workspace', 'Start a new task here and follow its progress after submitting it.'),
      nav('new-task', 'New Task', 'Return to a fresh task from anywhere in Praxys.'),
      nav('task-composer', 'Task composer', 'Describe the result you want. You can attach a file or select knowledge bases before sending.'),
      nav('knowledge', 'Knowledge Bases', 'Add or manage sources that tasks can use as context.'),
      nav('help', 'Help', 'Ask a question or replay the tour at any time.'),
    ],
  },
  task: {
    title: 'Task',
    summary: 'Review a task, its progress, results, and available follow-up actions.',
    commonTasks: ['How do I follow task progress?', 'Where are task results?', 'Can I ask a follow-up?'],
    steps: [main('Task details', 'Review the task activity and result in this workspace.'), nav('new-task', 'New Task', 'Start another task when you are ready.')],
  },
  chat: {
    title: 'Chat',
    summary: 'Ask questions in chat without creating a multi-step task.',
    commonTasks: ['When should I use chat instead of a task?', 'How do I start a new chat?'],
    steps: [nav('chat-workspace', 'Chat workspace', 'Ask a question and continue the conversation here.'), nav('new-task', 'New Task', 'Use a task for work that needs tools or multiple steps.')],
  },
  search: {
    title: 'Search Tasks',
    summary: 'Find and filter tasks you have created.',
    commonTasks: ['How do I find an older task?', 'Can I filter by status?'],
    steps: [focus('.praxys-library-search-row', 'Find a task', 'Search your task history by keyword.'), focus('.praxys-library-filters', 'Filter results', 'Narrow tasks by status and sort order.'), nav('search', 'Search', 'Return to task search from any view.')],
  },
  library: {
    title: 'Library',
    summary: 'Browse saved work and shared library items.',
    commonTasks: ['How do I find saved work?', 'What can I keep in the library?'],
    steps: [focus('.praxys-view-header', 'Library', 'Browse the work you saved for later.'), focus('.praxys-library-search-row', 'Find saved work', 'Search your saved results when the library has items.'), nav('library', 'Library', 'Open your library from the sidebar.')],
  },
  schedules: {
    title: 'Schedules',
    summary: 'Review and manage scheduled agent work.',
    commonTasks: ['Where can I see scheduled runs?', 'How do I schedule a task?'],
    steps: [focus('.praxys-view-header', 'Schedules', 'Review your scheduled work and its run history.'), nav('schedules', 'Schedules', 'Open schedules from the sidebar.')],
  },
  knowledge: {
    title: 'Knowledge Bases',
    summary: 'Manage the sources available to tasks and agents.',
    commonTasks: ['How do I add a knowledge base?', 'How does a task use a knowledge base?'],
    steps: [focus('.kb-header', 'Knowledge Bases', 'Create and manage sources for task context.'), focus('.kb-filter-bar', 'Find a source', 'Search or filter knowledge bases here.'), nav('knowledge', 'Knowledge Bases', 'Return here to manage your sources.')],
  },
  agents: {
    title: 'Agents',
    summary: 'Review, create, and configure reusable agents.',
    commonTasks: ['How do I create an agent?', 'Where can I review agent activity?'],
    steps: [nav('agent-header', 'Agents', 'Review agents and their activity here.'), nav('agent-create', 'New Agent', 'Create a reusable agent for a specific workflow.'), nav('agents', 'Agents', 'Open the agents workspace from the sidebar.')],
  },
  project: {
    title: 'Project',
    summary: 'Group tasks and knowledge bases around a shared goal.',
    commonTasks: ['How do I add a task to this project?', 'Can I attach a knowledge base?'],
    steps: [focus('.praxys-project-view-header', 'Project workspace', 'Keep related tasks and context together in this project.'), nav('task-composer', 'Project task', 'Create work in the current project here.')],
  },
  features: {
    title: 'Feature Requests',
    summary: 'Browse ideas and submit feedback about Praxys.',
    commonTasks: ['How do I request a feature?', 'How can I follow a request?'],
    steps: [focus('.praxys-feature-header', 'Feature Requests', 'Browse and submit product ideas here.'), focus('.praxys-feature-tabs-card', 'Browse ideas', 'Switch between requests and your own submissions.'), nav('features', 'Feature Requests', 'Open this area from the sidebar.')],
  },
  profile: {
    title: 'Profile',
    summary: 'Review your account and archived work.',
    commonTasks: ['Where is my archived work?', 'How do I restore an archived task?'],
    steps: [focus('.praxys-profile-summary', 'Profile', 'Review your account details here.'), focus('.praxys-profile-archives', 'Archived work', 'Review or restore archived tasks and knowledge bases here.')],
  },
  admin: {
    title: 'Admin',
    summary: 'Manage users, beta invitations, and administrative settings.',
    commonTasks: ['How do I send a beta invite?', 'Can I resend an invitation?'],
    steps: [focus('.praxys-admin-tabs', 'Administration', 'Choose an area to manage.'), nav('beta-invites-tab', 'Beta Invites', 'Create, resend, and revoke beta invitations here.')],
  },
  help: {
    title: 'Help',
    summary: 'Ask the help assistant, replay a tour, or open product resources.',
    commonTasks: ['Show me how Praxys works.', 'Where can I find documentation?'],
    steps: [focus('.praxys-help-actions', 'Help actions', 'Ask the assistant or replay this tour.'), focus('.praxys-help-grid', 'Resources', 'Find documentation and policy links here.')],
  },
};

export const getHelpGuide = (view: string): HelpGuide => HELP_GUIDES[view] || HELP_GUIDES.home;
